// @flow
import React, {useContext, useEffect, useState} from "react";
import * as PropTypes from "prop-types";
import ArchiverClientApi from "../API/archiverClientApi";
import {gotContactsForDepartment, gotLocations} from "../ACTIONS/clientActions";
import ErrorPopup from "../GUI_COMMON/COMPONENTS/ErrorPopup";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import HelpWithOnClick from "../GUI_COMMON/COMPONENTS/HelpWithOnClick";

/**
 * Note that tabIndex here is incremented once as there are 2 selects here (and 3 if showContacts is TRUE)
 */
const LocationSelector = ({name="", onChange, locationLabel, locationValue, departmentValue, contactValue, required, readOnly=false, 
                              showContact=true, withDepartmentsOnly=false, onIsValid, tabIndex,
                              showOffPremises=false, onHelpClicked, contactDefaultText="(select a contact)"}) => {
    
    const [globalState, dispatch] = useContext(StoreContext);
    const [locationInputClass, setLocationInputClass] = useState("");
    const [deptInputClass, setDeptInputClass] = useState("form-control li-form-field");
    const [selectedDeptValue,setSelectedDeptValue] = useState(departmentValue);
    const [selectedContactValue, setSelectedContactValue] = useState(contactValue);
    const [selectedLocation, setSelectedLocation] = useState(locationValue);
    
    useEffect( () => {
        if(!globalState.locations || globalState.locations.length===0) {
            // get the enabled and disabled locations (we filter based on readonly)
            ArchiverClientApi.getLocations(false).then( locations => {
                dispatch(gotLocations(locations));      // locations and their departments
            })
            .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError));
        }    
    },[globalState.locations]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        const inputClass = LIUtils.getValidationClassGivenValue(locationValue, required, readOnly);
        setLocationInputClass(inputClass)

        setSelectedContactValue(-1); // none
        const selectedLocation = globalState.locations.find( item =>item.id === locationValue);
        setSelectedDeptValue(-1);  // none
        setSelectedLocation(selectedLocation);
        updateLocationInputClass(selectedLocation);
        if(!selectedLocation) {
            updateDeptInputClass(selectedLocation,-1); // always update the department class
            setSelectedDeptValue(-1);
            return;
        }
        if (selectedLocation.departments?.length === 0) { // no departments
            setSelectedDeptValue(-1);
        }
        else if (selectedLocation.departments?.length === 1) { // preselect the only department in the list
            const sel = document.getElementById(name+"-destDepartment");
            sel.value = selectedLocation.departments[0].id
            fireEvent(sel, 'change'); // manually fire the change event.
            setSelectedDeptValue(selectedLocation.departments[0].id);
        }

        updateDeptInputClass(selectedLocation,departmentValue); // always update the department class
        setSelectedDeptValue(departmentValue);

        if(departmentValue && departmentValue>-1 && showContact) {
            ArchiverClientApi.getContactsForDepartment(departmentValue).then(contacts => {
                dispatch(gotContactsForDepartment(contacts));
            })
            .catch( errorResponse => ErrorPopup.showError("Error",errorResponse.error))
        }

        setSelectedContactValue(contactValue);

    }, [globalState.locations,locationValue,departmentValue,contactValue,required,readOnly]); // eslint-disable-line react-hooks/exhaustive-deps
    
    function updateDeptInputClass(selectedLocation,deptId) {
        let inputClass="";
        if(!selectedLocation || selectedLocation===-1) { // not set
            inputClass = LIUtils.getValidationClassGivenValue(null, required, readOnly);
            setDeptInputClass(inputClass);
        }
        if(shouldDeptBeDisabled(selectedLocation)) {
            inputClass = LIUtils.getValidationClassGivenValue(null, required, readOnly=true);
            setDeptInputClass(inputClass); // - always bad
        }
        else { // either good or bad depending on selection
            inputClass = LIUtils.getValidationClassGivenValue(deptId, required, readOnly);
            setDeptInputClass(inputClass); // maybe good or bad
        }
        if(onIsValid) { // update parent if they care
            onIsValid(LIUtils.isValidationClassValid(inputClass));
        }  
    }
    
    function updateLocationInputClass(selectedLocation) {
        let inputClass = LIUtils.getValidationClassGivenValue(selectedLocation?.id, required, readOnly);
        setLocationInputClass(inputClass);
        if(onIsValid) { // update parent if they care
            onIsValid(LIUtils.isValidationClassValid(inputClass));
        }  
    }
    
    function shouldDeptBeDisabled(selectedLocation):boolean {
        if(!selectedLocation || selectedLocation===-1) {
            return true;
        }
        if(selectedLocation?.departments===null || selectedLocation?.departments?.length<2) {
            return true;
        }
        return false;
    }
    
    function fireEvent(element,event){
        if (document.createEventObject){
            // dispatch for IE
            const evt = document.createEventObject();
            return element.fireEvent('on'+event,evt)
        }
        else{
            // dispatch for firefox + others
            const evt = document.createEvent("HTMLEvents");
            evt.initEvent(event, true, true ); // event type,bubbling,cancelable
            return !element.dispatchEvent(evt);
        }
    }
    
    // useEffect( () => {
    //     //const selectedLocation = globalState.locations.find( item =>item.id === locationValue);
    //     //setSelectedLocation(selectedLocation);  // for use by disable dept button
    //     updateDeptInputClass(selectedLocation,departmentValue); // always update the department class
    //     // if(departmentValue===selectedDeptValue) { // no change
    //     //     return;
    //     // }
    //     setSelectedDeptValue(departmentValue);
    //    
    //     if(departmentValue && departmentValue>-1 && showContact) {
    //         ArchiverClientApi.getContactsForDepartment(departmentValue).then(contacts => {
    //             dispatch(gotContactsForDepartment(contacts));
    //         })
    //         .catch( errorResponse => ErrorPopup.showError("Error",errorResponse.error))
    //     }
    // },[departmentValue,required,readOnly]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        setSelectedContactValue(contactValue);
    }, [contactValue]);
    
    function dumpTopLevelOptionsList(locations) {
        if(!locations || locations.length===0) {
            return <option disabled>(no locations available)</option>;
        }
        if(withDepartmentsOnly) {
            locations = locations.filter( l => l.departments!==null && l.departments.length>0);
        }
        if(!showOffPremises) { // hide off-premises locations
            locations = locations.filter( l => l.offPremises===false); // hide
        }
        return locations.map(item => {
            if(readOnly===true || item.isEnabled) { // only show if it's enabled and we're not readonly
                return <option key={item.id} value={item.id}>{item.name} {item.offPremises ? "(off-premises)" : ""}</option>;
            }
            return null;
        });
    }

    function dumpDepartmentLevelOptionsList(locations, selectedLocationId) {
        const destDeptSection = document.getElementById("destDeptSection");
        
        if(!locations || locations.length===0) {
            return <option value={-1} disabled>(select a location first)</option>
        } 
        if(!selectedLocationId || selectedLocationId===-1) {
            return <option value={-1} disabled>(select a department)</option>
        }
        
        const selectedLocation = locations.find( item =>item.id === selectedLocationId);
        if(!selectedLocation || (selectedLocation.isEnabled===false && readOnly===false)) { // force a department re-selection
            return <option value={-1} disabled>(select a department)</option>;
        }
        
        if (selectedLocation.departments===null || selectedLocation.departments.length===0) {
            if(destDeptSection!=null) destDeptSection.style.display = "none";
            const destContactSection = document.getElementById("destContactSection"); // hide the contacts
            if(destContactSection!=null) destContactSection.style.display = "none";
            return;
        }
        
        if(destDeptSection!=null) destDeptSection.style.display = "block";
        
        const optionList = selectedLocation.departments.map(item => {
            if(readOnly===true || item.isEnabled) { // only show if it's enabled and we're not readonly
                return <option key={item.id} value={item.id}>{item.name}</option>;
            }
            return null;
        });
        // add the default option
        optionList.unshift(<option key="-1" value={-1} disabled>{selectedLocationId === -1 ? "(select a location first)" : "(select a department)"}</option>);
        
        return optionList;
    }

    function dumpContactLevelOptionsList(contacts, selectedDepartmentId) {
        const destContactSection = document.getElementById("destContactSection");
    
        if(!contacts || contacts.length===0 || selectedDepartmentId===-1) {             
            if(destContactSection!=null) destContactSection.style.display = "none";
            return ; //<option disabled>(no contacts available)</option>;
        }

        if(destContactSection!=null) destContactSection.style.display = "block";
        
        const optionList = contacts.map(item => <option key={item.id} value={item.id}>{item.fullName}</option>);
        // if(optionList.length===1) { // preselect this single option
        //     if(document.getElementById("destContact")!=null) {
        //         document.getElementById("destContact").value = contacts[0].id;
        //     }
        // }
        // else {
            optionList.unshift(<option key="-1" value={-1}>{selectedDepartmentId === -1 ? "(select a department first)" : contactDefaultText}</option>);
        //}
        return optionList;
    }

    function onLocationChanged(e) {
        const loc = e.target.value;
        if(Number(loc)===locationValue) { // no change
            return;
        }
        onChange(e);
    }

    function onDeptChanged(e) {
       const dept = e.target.value;
       if(Number(dept)===departmentValue) { // no change
           return;
       }
       onChange(e);
    }

    function onContactChanged(e) {
        const contact = e.target.value;
        if(Number(contact)===contactValue) { // no change
            return;
        }
        onChange(e);
    }
    
    return (
          <div className="form-group m-0">
              
              <div className="row">
                  <div className="col pl-0">
                      {/* LOCATIONS */}
                      <label className="li-form-label mr-1" htmlFor={name+"-destLocation"}>{locationLabel??"Destination Location"}</label>
                      {onHelpClicked && <HelpWithOnClick small onHelpClicked={onHelpClicked}/>}
                      <select onChange={onLocationChanged} id={name+"-destLocation"} name={name+"-destLocation"} value={locationValue??-1} className={locationInputClass+" li-select-input-class"} required={required}
                            disabled={readOnly} tabIndex={tabIndex}>
                          <option value={-1} disabled>{readOnly ? "No Location" : "(select a location)"}</option>
                          {dumpTopLevelOptionsList(globalState.locations)}
                      </select>
                  </div>
                  <div id={name+"-destDeptSection"} className={(readOnly && (!departmentValue||departmentValue===-1)) ? "d-none":""}>
                      {/* DEPARTMENTS */}
                      <label className="li-form-label" htmlFor={name+"-destDepartment"}>Department</label>
                      <select onChange={onDeptChanged} id={name+"-destDepartment"} name={name+"-destDepartment"} value={selectedDeptValue??-1} className={deptInputClass}
                            disabled={readOnly || locationValue===-1 || shouldDeptBeDisabled(selectedLocation)} tabIndex={tabIndex?tabIndex+1:null}>
                          {dumpDepartmentLevelOptionsList(globalState.locations,locationValue)}
                      </select>
                  </div>
              </div>
              
              {/* CONTACT */}
              {showContact &&
                  <section id={name+"-destContactSection"}
                           className={(readOnly && (!contactValue || contactValue === -1)) ? "d-none" : ""}>
                      <label className="li-form-label" htmlFor={name+"-destContact"}>Person</label>
                      <select onChange={onContactChanged} id={name+"-destContact"} name={name+"-destContact"} value={selectedContactValue} className="form-control li-form-field li-border-dark"
                              disabled={readOnly || departmentValue === -1} tabIndex={tabIndex?tabIndex+2:null}>
                          {dumpContactLevelOptionsList(globalState.contacts, departmentValue)}
                      </select>
                  </section>
              }
              
          </div>
    );
}

LocationSelector.propTypes = {
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    locationValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    departmentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contactValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    required: PropTypes.bool,
    focus: PropTypes.bool,
    readOnly: PropTypes.bool,
    showContact: PropTypes.bool,
    withDepartmentsOnly: PropTypes.bool,
    locationLabel: PropTypes.string,
    onIsValid: PropTypes.func,
    tabIndex: PropTypes.number,
    showOffPremises: PropTypes.bool,
    onHelpClicked: PropTypes.func,
    contactDefaultText: PropTypes.string
};

export default LocationSelector;